import type { Lang } from '../i18n'

/**
 * The home page, in both languages.
 *
 * Swedish and English are peers here, not a translation and its source: Ronin's own
 * resx files are written the same way. Every sentence follows the app's rules, which
 * are a regulatory boundary and not a style preference: it is a diary, it never
 * advises, and no number is ever graded. Say "diary", "write down", "your own record".
 * Never "manage", "control" or "keep safe". No em-dashes, in either language.
 */

export type HomeCopy = {
  nav: { home: string; features: string; about: string }

  heroEyebrow: string
  heroTitle: string
  heroSub: string
  leads: string[]

  /** The small line above each store name, once the app is actually in the stores. */
  storeSmallApple: string
  storeSmallGoogle: string
  storeComing: string
  storeNote: string

  phoneCaption: string
  mock: {
    day: string
    date: string
    value: string
    unit: string
    time: string
    summary: string
    rows: { time: string; text: string; serif?: boolean }[]
    quick: { glucose: string; insulin: string; carbs: string; note: string }
    tabs: { today: string; history: string; settings: string }
    stripLabel: string
  }

  featTitle: string
  featIntro: string
  features: { title: string; body: string }[]

  aboutTitle: string
  aboutLead: string
  aboutItems: { title: string; body: string }[]
}

const en: HomeCopy = {
  nav: { home: 'Home', features: 'Features', about: 'About' },

  heroEyebrow: 'Diabetes diary',
  heroTitle: 'A diary for adults with type 1 diabetes.',
  heroSub: 'You write down glucose, insulin, carbs and notes. That is all it does.',
  leads: [
    'Your diary stays on your phone. No account.',
    'Photograph the plate and get a carb proposal you correct yourself.',
    'Log from your Apple Watch or Wear OS watch.',
    'Free, with no ads and nothing to buy.',
  ],

  storeSmallApple: 'Download on the',
  storeSmallGoogle: 'Get it on',
  storeComing: 'Coming to the App Store and Google Play.',
  storeNote: 'iPhone and Android. Swedish and English.',

  phoneCaption:
    'Today in Ronin: the latest reading, the day so far, and everything written down in order.',
  mock: {
    day: 'Wednesday',
    date: '26 August',
    value: '6.4',
    unit: 'mmol/L',
    time: '21:40',
    summary:
      'Four readings, eleven units of insulin and 105 g of carbs, written down between 07:15 and 21:40.',
    rows: [
      { time: '21:40', text: 'Glucose 6.4' },
      { time: '17:30', text: '“Long walk after work”', serif: true },
      { time: '12:45', text: 'Carbs 60 g · Pasta' },
    ],
    quick: { glucose: 'Glucose', insulin: 'Insulin', carbs: 'Carbs', note: 'Note' },
    tabs: { today: 'Today', history: 'History', settings: 'Settings' },
    stripLabel: 'Glucose over the day',
  },

  featTitle: 'What Ronin does',
  featIntro:
    'A diary, kept the way a paper one was: you write down what happened, and you can read it back. Nothing is scored and nothing is interpreted.',
  features: [
    {
      title: 'Photograph the plate',
      body: 'Point the camera at what you are about to eat and get a carb proposal, with the items the app thinks it saw. You correct the number before anything is written down. An estimate is never presented as a measurement, and a photo leaves your phone only if you have switched the feature on.',
    },
    {
      title: 'Four things to write down',
      body: 'Glucose, insulin, carbs and notes. Ketones can be added as a fifth in Settings. Every entry can be edited or removed.',
    },
    {
      title: 'On your wrist',
      body: 'The same four buttons on Apple Watch and Wear OS. Type the value on the watch and it lands in the diary on your phone. The watch keeps no diary and shows no values.',
    },
    {
      title: 'No scores, no streaks',
      body: 'No points and no colours telling you how you did. Values are never coloured, graded or interpreted.',
    },
    {
      title: 'Carbs two more ways',
      body: 'Search a food and count portions, or weigh it on the kitchen scale. Every way ends in a number you can change.',
    },
    {
      title: 'The month on one page',
      body: 'Each square is a day and the marks sit where the readings sat. Tap a day to read it back.',
    },
    {
      title: 'A target range, if you want one',
      body: 'Drawn as a band behind the day’s glucose. Display only, not a prescription. You set the range yourself.',
    },
    {
      title: 'Your units, your words, your file',
      body: 'mmol/L or mg/dL, your own insulin names, Swedish or English. Export a CSV for your care team whenever you want one.',
    },
  ],

  aboutTitle: 'About Ronin',
  aboutLead:
    'Ronin is a diary for adults with type 1 diabetes. You write down glucose, insulin, carbs and notes. That is all it does. It is free, from the people who make DiabetesNinja.',
  aboutItems: [
    {
      title: 'No advice, ever',
      body: 'Ronin never suggests a dose and never gives medical advice. Talk to your care team about treatment.',
    },
    {
      title: 'Your diary stays on the phone',
      body: 'Everything you write down stays on this phone and in the phone’s own backup. Ronin has no account. The app sends an anonymous daily ping and crash reports, and a photo of a plate only when you have chosen to. Never your diary. The privacy page has the details.',
    },
    {
      title: 'It asks for almost nothing',
      body: 'The camera only when you photograph a plate, and only if you have switched that on. No location, no contacts. The app uses the network for the ping, the crash reports and that photo, nothing else.',
    },
  ],
}

const sv: HomeCopy = {
  nav: { home: 'Hem', features: 'Funktioner', about: 'Om' },

  heroEyebrow: 'Diabetesdagbok',
  heroTitle: 'En dagbok för vuxna med typ 1-diabetes.',
  heroSub: 'Du skriver ner glukos, insulin, kolhydrater och anteckningar. Mer än så gör den inte.',
  leads: [
    'Din dagbok stannar i telefonen. Inget konto.',
    'Fotografera tallriken och få ett kolhydratförslag som du själv rättar.',
    'Logga från din Apple Watch eller Wear OS-klocka.',
    'Gratis, utan reklam och utan något att köpa.',
  ],

  storeSmallApple: 'Ladda ner på',
  storeSmallGoogle: 'Finns på',
  storeComing: 'Kommer till App Store och Google Play.',
  storeNote: 'iPhone och Android. Svenska och engelska.',

  phoneCaption:
    'Idag i Ronin: senaste värdet, dagen så långt och allt du skrivit ner i ordning.',
  mock: {
    day: 'Onsdag',
    date: '26 augusti',
    value: '6,4',
    unit: 'mmol/L',
    time: '21:40',
    summary:
      'Fyra värden, elva enheter insulin och 105 g kolhydrater, nedskrivet mellan 07:15 och 21:40.',
    rows: [
      { time: '21:40', text: 'Glukos 6,4' },
      { time: '17:30', text: '”Lång promenad efter jobbet”', serif: true },
      { time: '12:45', text: 'Kolhydrater 60 g · Pasta' },
    ],
    quick: {
      glucose: 'Glukos',
      insulin: 'Insulin',
      carbs: 'Kolhydrater',
      note: 'Anteckning',
    },
    tabs: { today: 'Idag', history: 'Historik', settings: 'Inställningar' },
    stripLabel: 'Glukos under dagen',
  },

  featTitle: 'Det här gör Ronin',
  featIntro:
    'En dagbok, förd som en dagbok på papper: du skriver ner vad som hände och kan läsa det igen. Ingenting poängsätts och ingenting tolkas.',
  features: [
    {
      title: 'Fotografera tallriken',
      body: 'Rikta kameran mot det du ska äta och få ett kolhydratförslag, med det som appen tror att den såg. Du rättar siffran innan något skrivs ner. En uppskattning presenteras aldrig som en mätning, och ett foto lämnar telefonen bara om du har slagit på funktionen.',
    },
    {
      title: 'Fyra saker att skriva ner',
      body: 'Glukos, insulin, kolhydrater och anteckningar. Ketoner kan läggas till som en femte under Inställningar. Varje inlägg kan ändras eller tas bort.',
    },
    {
      title: 'På handleden',
      body: 'Samma fyra knappar på Apple Watch och Wear OS. Skriv in värdet på klockan, så hamnar det i dagboken i telefonen. Klockan har ingen dagbok och visar inga värden.',
    },
    {
      title: 'Inga poäng, inga sviter',
      body: 'Inga poäng och inga färger som talar om hur du skötte dig. Värden färgas aldrig, betygsätts aldrig och tolkas aldrig.',
    },
    {
      title: 'Kolhydrater på två sätt till',
      body: 'Sök en mat och räkna portioner, eller väg den på köksvågen. Varje sätt slutar i en siffra som du kan ändra.',
    },
    {
      title: 'Månaden på en sida',
      body: 'Varje ruta är en dag och märkena sitter där värdena satt. Tryck på en dag för att läsa den igen.',
    },
    {
      title: 'Ett målområde, om du vill',
      body: 'Ritas som ett band bakom dagens glukos. Bara visning, ingen ordination. Du väljer området själv.',
    },
    {
      title: 'Dina enheter, dina ord, din fil',
      body: 'mmol/L eller mg/dL, dina egna insulinnamn, svenska eller engelska. Exportera en CSV till din vårdkontakt när du vill.',
    },
  ],

  aboutTitle: 'Om Ronin',
  aboutLead:
    'Ronin är en dagbok för vuxna med typ 1-diabetes. Du skriver ner glukos, insulin, kolhydrater och anteckningar. Mer än så gör den inte. Appen är gratis och görs av personerna bakom DiabetesNinja.',
  aboutItems: [
    {
      title: 'Aldrig några råd',
      body: 'Ronin föreslår aldrig en dos och ger aldrig medicinska råd. Prata med din vårdkontakt om behandlingen.',
    },
    {
      title: 'Din dagbok stannar i telefonen',
      body: 'Allt du skriver ner stannar i telefonen och i telefonens egen säkerhetskopia. Ronin har inget konto. Appen skickar en anonym daglig signal och kraschrapporter, och ett foto på en tallrik bara när du själv har valt det. Aldrig din dagbok. Detaljerna står på integritetssidan.',
    },
    {
      title: 'Den ber om nästan ingenting',
      body: 'Kameran bara när du fotograferar en tallrik, och bara om du har slagit på det. Ingen plats, inga kontakter. Appen använder nätet till signalen, kraschrapporterna och det fotot, inget annat.',
    },
  ],
}

export const homeCopy: Record<Lang, HomeCopy> = { sv, en }
